/**
 * 缓存工具类
 * 提供对 localStorage 和 sessionStorage 的兼容处理
 * 包括无痕模式下的降级方案
 */

// 内存缓存，用于在 Storage API 不可用时的降级方案
const memoryStorage: Record<string, Record<string, string>> = {
  local: {},
  session: {}
};

/**
 * 检查存储 API 是否可用
 * @param storageType 存储类型 ('localStorage' | 'sessionStorage')
 * @returns 存储 API 是否可用
 */
const isStorageAvailable = (storageType: 'localStorage' | 'sessionStorage'): boolean => {
  try {
    const storage = window[storageType];
    const testKey = `__storage_test__${Date.now()}`;
    storage.setItem(testKey, 'test');
    const result = storage.getItem(testKey) === 'test';
    storage.removeItem(testKey);
    return result;
  } catch {
    return false;
  }
};

// 检查 localStorage 和 sessionStorage 是否可用
const localStorageAvailable = isStorageAvailable('localStorage');
const sessionStorageAvailable = isStorageAvailable('sessionStorage');

/**
 * 设置本地存储
 * @param key 键名
 * @param value 值
 * @param useSession 是否使用 sessionStorage (默认为 false，使用 localStorage)
 */
export const setStorage = <T>(key: string, value: T, useSession = false): void => {
  try {
    const storageType = useSession ? 'sessionStorage' : 'localStorage';
    const isAvailable = useSession ? sessionStorageAvailable : localStorageAvailable;
    const memoryType = useSession ? 'session' : 'local';
    
    // 序列化值
    const serializedValue = JSON.stringify(value);
    
    if (isAvailable) {
      // 如果存储 API 可用，则使用浏览器的存储 API
      window[storageType].setItem(key, serializedValue);
    } else {
      // 否则使用内存存储作为降级方案
      memoryStorage[memoryType][key] = serializedValue;
      console.warn(`${storageType} 不可用 (可能是无痕模式)，已降级为内存存储`);
    }
  } catch (error) {
    console.error('存储数据时出错:', error);
    // 出错时降级为内存存储
    const memoryType = useSession ? 'session' : 'local';
    memoryStorage[memoryType][key] = JSON.stringify(value);
  }
};

/**
 * 获取存储的值
 * @param key 键名
 * @param useSession 是否从 sessionStorage 获取 (默认为 false，从 localStorage 获取)
 * @param defaultValue 默认值，当键不存在时返回
 * @returns 存储的值或默认值
 */
export const getStorage = <T>(key: string, useSession = false, defaultValue?: T): T | undefined => {
  try {
    const storageType = useSession ? 'sessionStorage' : 'localStorage';
    const isAvailable = useSession ? sessionStorageAvailable : localStorageAvailable;
    const memoryType = useSession ? 'session' : 'local';
    
    let value: string | null = null;
    
    if (isAvailable) {
      // 如果存储 API 可用，则从浏览器的存储 API 获取
      value = window[storageType].getItem(key);
    } else {
      // 否则从内存存储获取
      value = memoryStorage[memoryType][key] || null;
    }
    
    // 如果值不存在，返回默认值
    if (value === null) {
      return defaultValue;
    }
    
    // 反序列化值
    return JSON.parse(value);
  } catch (error) {
    console.error('获取存储数据时出错:', error);
    return defaultValue;
  }
};

/**
 * 移除存储的值
 * @param key 键名
 * @param useSession 是否从 sessionStorage 移除 (默认为 false，从 localStorage 移除)
 */
export const removeStorage = (key: string, useSession = false): void => {
  try {
    const storageType = useSession ? 'sessionStorage' : 'localStorage';
    const isAvailable = useSession ? sessionStorageAvailable : localStorageAvailable;
    const memoryType = useSession ? 'session' : 'local';
    
    if (isAvailable) {
      // 如果存储 API 可用，则从浏览器的存储 API 移除
      window[storageType].removeItem(key);
    } else {
      // 否则从内存存储移除
      delete memoryStorage[memoryType][key];
    }
  } catch (error) {
    console.error('移除存储数据时出错:', error);
  }
};

/**
 * 清除所有存储
 * @param useSession 是否清除 sessionStorage (默认为 false，清除 localStorage)
 */
export const clearStorage = (useSession = false): void => {
  try {
    const storageType = useSession ? 'sessionStorage' : 'localStorage';
    const isAvailable = useSession ? sessionStorageAvailable : localStorageAvailable;
    const memoryType = useSession ? 'session' : 'local';
    
    if (isAvailable) {
      // 如果存储 API 可用，则清除浏览器的存储 API
      window[storageType].clear();
    } else {
      // 否则清除内存存储
      memoryStorage[memoryType] = {};
    }
  } catch (error) {
    console.error('清除存储数据时出错:', error);
  }
};

export default {
  setStorage,
  getStorage,
  removeStorage,
  clearStorage
};
